(() => {
  if (window.NumeraioNewsletterBanner) {
    window.NumeraioNewsletterBanner.init();
    return;
  }

  const storageKey = 'numeraio-newsletter-banner-dismissed-v2';
  const instances = new Map();
  const overlaySelector = [
    '#shopify-pc__banner', '#shopify-pc__prefs', '.menu-overlay',
    '.minicart-drawer', '.filters-drawer', '[data-product-lightbox]',
    '[data-calendar-popup]', '.locale-dropdown',
    '[role="dialog"][aria-modal="true"]', 'dialog'
  ].join(',');
  let dismissedThisPage = false;

  const isVisible = (element) => {
    if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const hasBeenDismissed = () => {
    if (dismissedThisPage) return true;
    try { return window.sessionStorage.getItem(storageKey) === '1'; }
    catch (_error) { return false; }
  };

  const rememberDismissal = () => {
    dismissedThisPage = true;
    try { window.sessionStorage.setItem(storageKey, '1'); }
    catch (_error) { /* The current page still remembers dismissal without storage. */ }
  };

  const initBanner = (banner) => {
    if (instances.has(banner)) return;
    const form = document.getElementById(banner.dataset.formId);
    const email = form?.querySelector('input[type="email"]');
    if (!form || !email || form.querySelector('[aria-invalid="true"]')) {
      if (form?.querySelector('[role="status"]')) rememberDismissal();
      return;
    }
    const designMode = Boolean(window.Shopify?.designMode);
    if (!designMode && hasBeenDismissed()) return;

    const newsletter = form.closest('.numeraio-newsletter') || form;
    const controller = new AbortController();
    const { signal } = controller;
    const observedOverlays = new Map();
    const readyAt = Date.now() + (designMode ? 0 : 2400);
    let frame = 0;
    let timer = 0;
    let dismissed = false;
    let hasShown = false;
    let loadingPrivacy = false;
    let newsletterVisible = false;
    let previousFocus = null;

    document.body.appendChild(banner);

    const privacyPending = () => {
      if (designMode) return false;
      const privacy = window.Shopify?.customerPrivacy;
      // Fail closed until the API is ready, including delayed cookie-banner loads.
      if (typeof privacy?.shouldShowBanner !== 'function' || typeof privacy?.currentVisitorConsent !== 'function') return true;
      try {
        const consent = privacy.currentVisitorConsent();
        const answered = ['analytics', 'marketing', 'preferences'].some(
          (key) => consent[key] === 'yes' || consent[key] === 'no'
        );
        return privacy.shouldShowBanner() && !answered;
      } catch (_error) { return true; }
    };

    const update = () => {
      frame = 0;
      if (dismissed || !banner.isConnected) return;
      const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
      const blocked = Date.now() < readyAt || document.visibilityState === 'hidden' ||
        privacyPending() || newsletterVisible || editing ||
        banner.hasAttribute('inert') ||
        document.body.matches('.menu-active, .minicart-open, .filters-open, .lightbox-active, .calendar-popup-open') ||
        Array.from(observedOverlays.keys()).some(isVisible);
      banner.hidden = Boolean(blocked);
      if (!blocked && !hasShown) {
        hasShown = true;
        previousFocus = document.activeElement;
      }
    };

    const schedule = () => {
      if (!frame && !dismissed) frame = window.requestAnimationFrame(update);
    };

    const watchOverlays = () => {
      for (const [element, observer] of observedOverlays) {
        if (!element.isConnected) { observer.disconnect(); observedOverlays.delete(element); }
      }
      document.querySelectorAll(overlaySelector).forEach((element) => {
        if (observedOverlays.has(element)) return;
        const observer = new MutationObserver(schedule);
        observer.observe(element, { attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'class', 'style', 'open'] });
        observedOverlays.set(element, observer);
      });
      schedule();
    };

    const requestPrivacy = () => {
      if (!designMode && !window.Shopify?.customerPrivacy && !loadingPrivacy && window.Shopify?.loadFeatures) {
        loadingPrivacy = true;
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
          loadingPrivacy = false;
          schedule();
        });
      }
      schedule();
    };

    // Observe overlay attributes only, never the hands' per-frame GSAP styles.
    const domObserver = new MutationObserver(watchOverlays);
    domObserver.observe(document.body, { childList: true, subtree: true });
    const bodyObserver = new MutationObserver(schedule);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    const inertObserver = new MutationObserver(schedule);
    inertObserver.observe(banner, { attributes: true, attributeFilter: ['inert'] });
    const intersectionObserver = new IntersectionObserver((entries) => {
      newsletterVisible = entries.some((entry) => entry.isIntersecting);
      schedule();
    });
    intersectionObserver.observe(newsletter);

    const stop = () => {
      controller.abort();
      window.clearTimeout(timer);
      window.cancelAnimationFrame(frame);
      domObserver.disconnect();
      bodyObserver.disconnect();
      inertObserver.disconnect();
      intersectionObserver.disconnect();
      observedOverlays.forEach((observer) => observer.disconnect());
      dismissed = true;
      banner.hidden = true;
    };

    const dismiss = (restoreFocus = false) => {
      if (!designMode) rememberDismissal();
      if (restoreFocus && banner.contains(document.activeElement)) {
        const target = previousFocus?.isConnected && previousFocus !== document.body ? previousFocus : document.getElementById('MainContent');
        target?.focus({ preventScroll: true });
      }
      stop();
    };

    banner.querySelector('[data-newsletter-banner-close]').addEventListener('click', () => dismiss(true), { signal });
    banner.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); dismiss(true); }
    }, { signal });
    banner.querySelector('[data-newsletter-banner-signup]').addEventListener('click', (event) => {
      event.preventDefault();
      dismiss();
      newsletter.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      email.focus({ preventScroll: true });
    }, { signal });

    document.addEventListener('visitorConsentCollected', schedule, { signal });
    document.addEventListener('visibilitychange', schedule, { signal });
    document.addEventListener('focusin', schedule, { signal });
    document.addEventListener('focusout', schedule, { signal });
    window.addEventListener('pageshow', requestPrivacy, { signal });
    window.addEventListener('load', requestPrivacy, { signal });

    instances.set(banner, () => { stop(); banner.remove(); instances.delete(banner); });
    watchOverlays();
    requestPrivacy();
    timer = window.setTimeout(schedule, Math.max(0, readyAt - Date.now()));
  };

  const init = () => document.querySelectorAll('[data-newsletter-banner]').forEach(initBanner);
  window.NumeraioNewsletterBanner = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [banner, cleanup] of instances) {
      if (banner.dataset.sectionId === event.detail.sectionId) cleanup();
    }
  });
})();
